import { processingExperiment } from './processingExperiment';
import type { Command } from './service';
import type { Entity, Kind, Snapshot } from './types';
export interface Field {
  key: string;
  label: string;
  required?: boolean;
  choices?: string[];
  number?: boolean;
}
export interface ModuleSpec {
  kind: Kind;
  name: string;
  fields: Field[];
  roles: string[];
  transitions: Record<string, string[]>;
}
const managers = ['campusManager', 'groupOperator', 'regionManager'];
const educators = [...managers, 'teacher', 'researchLead'];
const serviceRoles = [...managers, 'studentService'];
const specs: ModuleSpec[] = [
  {
    kind: 'organization',
    name: '组织架构',
    fields: [
      {
        key: 'type',
        label: '组织类型',
        required: true,
        choices: ['集团', '区域', '校区'],
      },
      { key: 'parentUuid', label: '上级组织' },
    ],
    roles: [...managers, 'systemAdmin'],
    transitions: {
      draft: ['active'],
      active: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'policy',
    name: '模拟业务权限策略',
    fields: [
      {
        key: 'role',
        label: '业务角色代码',
        required: true,
        choices: [
          'groupLeader',
          'groupOperator',
          'regionManager',
          'campusManager',
          'researchLead',
          'studentService',
          'teacher',
          'reviewer',
          'deviceManager',
          'systemAdmin',
          'support',
        ],
      },
      {
        key: 'kind',
        label: '资源',
        required: true,
        choices: [
          'device',
          'recording',
          'clue',
          'case',
          'plan',
          'knowledge',
          'student',
          'followUp',
          'feedback',
          'job',
        ],
      },
      {
        key: 'read',
        label: '读取权限',
        required: true,
        choices: ['允许', '禁止'],
      },
      {
        key: 'write',
        label: '写入权限',
        required: true,
        choices: ['允许', '禁止'],
      },
    ],
    roles: ['systemAdmin', 'groupOperator'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'modelConfig',
    name: '模拟模型处理配置',
    fields: [
      {
        key: 'parallel',
        label: '分析并发数（1–3）',
        required: true,
        number: true,
      },
      { key: 'timeoutMs', label: '超时毫秒', required: true, number: true },
    ],
    roles: ['systemAdmin', 'groupOperator'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'subscription',
    name: '模拟服务与配额',
    fields: [
      {
        key: 'analysisLimit',
        label: '分析任务上限',
        required: true,
        number: true,
      },
      {
        key: 'unitPrice',
        label: '每分钟估算单价（分）',
        required: true,
        number: true,
      },
      { key: 'expiresAt', label: '服务到期时间', required: true },
      { key: 'release', label: '软件/固件版本说明', required: true },
    ],
    roles: ['systemAdmin', 'groupOperator'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'integration',
    name: '模拟接口与同步',
    fields: [
      {
        key: 'targetKind',
        label: '同步目标',
        required: true,
        choices: ['lesson', 'teacher'],
      },
      {
        key: 'sourceJson',
        label: '模拟来源 JSON（数组，含 key 和 title）',
        required: true,
      },
      {
        key: 'strategy',
        label: '冲突处理策略',
        required: true,
        choices: ['跳过', '确认更新'],
      },
    ],
    roles: ['systemAdmin', 'groupOperator'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'evaluation',
    name: '分析质量样本',
    fields: [
      { key: 'recordingUuid', label: '样本录音', required: true },
      {
        key: 'subject',
        label: '场景',
        required: true,
        choices: ['数学', '语文', '英语'],
      },
      {
        key: 'prediction',
        label: '预测',
        required: true,
        choices: ['命中', '未命中'],
      },
      {
        key: 'truth',
        label: '人工标注',
        required: true,
        choices: ['成立', '不成立', '无法判断'],
      },
    ],
    roles: [...managers, 'reviewer', 'researchLead'],
    transitions: { draft: ['confirmed'] },
  },
  {
    kind: 'zone',
    name: '模拟区域与围栏',
    fields: [
      {
        key: 'points',
        label: '多边形坐标 JSON，至少三个 [x,y] 点，范围0–1',
        required: true,
      },
      { key: 'purpose', label: '区域用途', required: true },
    ],
    roles: [...managers, 'deviceManager'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },

  {
    kind: 'model',
    name: '型号与能力',
    fields: [
      {
        key: 'read',
        label: '录音读取',
        required: true,
        choices: ['已验证', '待验证', '不支持'],
      },
      {
        key: 'remote',
        label: '远程配置',
        required: true,
        choices: ['已验证', '待验证', '不支持'],
      },
      {
        key: 'location',
        label: '定位回传',
        required: true,
        choices: ['已验证', '待验证', '不支持'],
      },
    ],
    roles: [...managers, 'deviceManager', 'systemAdmin'],
    transitions: {
      draft: ['published'],
      confirmed: ['draft'],
      pending: ['draft'],
      published: ['draft'],
    },
  },
  {
    kind: 'station',
    name: '采集站',
    fields: [
      {
        key: 'connection',
        label: '连接方式',
        required: true,
        choices: ['蓝牙排队', '有线读取', '远程回传'],
      },
    ],
    roles: [...managers, 'deviceManager'],
    transitions: {
      draft: ['active'],
      active: ['disabled'],
      disabled: ['draft'],
    },
  },

  {
    kind: 'inventory',
    name: '配件与库存',
    fields: [
      { key: 'quantity', label: '库存数量', required: true, number: true },
    ],
    roles: [...managers, 'deviceManager'],
    transitions: {},
  },
  {
    kind: 'repair',
    name: '故障与维修',
    fields: [
      { key: 'deviceUuid', label: '设备', required: true },
      { key: 'reason', label: '故障说明', required: true },
    ],
    roles: [...managers, 'deviceManager', 'support', 'teacher'],
    transitions: {
      draft: ['inProgress'],
      inProgress: ['pendingAcceptance'],
      pendingAcceptance: ['closed', 'inProgress'],
      closed: ['reopened'],
      reopened: ['inProgress'],
    },
  },
  {
    kind: 'template',
    name: '分析模板',
    fields: [
      {
        key: 'scene',
        label: '适用场景',
        required: true,
        choices: ['课堂教学', '课后沟通', '教学复盘'],
      },
      {
        key: 'output',
        label: '输出内容',
        required: true,
        choices: ['沟通线索', '教学观察', '服务待办'],
      },
    ],
    roles: [...managers, 'researchLead', 'systemAdmin'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'rule',
    name: '分析规则',
    fields: [
      { key: 'keyword', label: '关注关键词', required: true },
      { key: 'exclusion', label: '排除条件', required: true },
      { key: 'example', label: '测试样本', required: true },
    ],
    roles: [...managers, 'systemAdmin', 'reviewer'],
    transitions: {
      draft: ['published'],
      published: ['disabled'],
      disabled: ['draft'],
    },
  },
  {
    kind: 'correction',
    name: '更正与复议',
    fields: [
      { key: 'recordingUuid', label: '来源录音', required: true },
      { key: 'correctedText', label: '更正后首段文本', required: true },
    ],
    roles: [...educators, 'reviewer'],
    transitions: {
      draft: ['pendingReview'],
      pendingReview: ['accepted', 'rejected'],
      rejected: ['draft'],
    },
  },
  {
    kind: 'lesson',
    name: '课程与课次',
    fields: [
      {
        key: 'subject',
        label: '学科',
        required: true,
        choices: ['数学', '语文', '英语'],
      },
      { key: 'goals', label: '课程目标', required: true },
      { key: 'recordingUuid', label: '关联录音' },
    ],
    roles: educators,
    transitions: { draft: ['inProgress'], inProgress: ['completed'] },
  },
  {
    kind: 'plan',
    name: '教师改进计划',
    fields: [
      { key: 'goal', label: '改进目标', required: true },
      { key: 'recordingUuid', label: '录音依据', required: true },
      { key: 'mentor', label: '导师', required: true },
      { key: 'dueAt', label: '实践期限', required: true },
    ],
    roles: educators,
    transitions: {
      draft: ['inProgress'],
      inProgress: ['pendingAcceptance', 'cancelled'],
      pendingAcceptance: ['completed', 'inProgress'],
    },
  },
  {
    kind: 'followUp',
    name: '学生服务跟进',
    fields: [
      { key: 'studentUuid', label: '学生', required: true },
      { key: 'recordingUuid', label: '沟通依据', required: true },
      { key: 'promise', label: '待跟进承诺', required: true },
      { key: 'dueAt', label: '截止时间', required: true },
    ],
    roles: serviceRoles,
    transitions: {
      draft: ['confirmed', 'excluded'],
      confirmed: ['inProgress'],
      inProgress: ['completed'],
    },
  },
  {
    kind: 'feedback',
    name: '家长反馈',
    fields: [
      { key: 'studentUuid', label: '学生', required: true },
      { key: 'followUpUuid', label: '已完成跟进', required: true },
      { key: 'body', label: '反馈正文', required: true },
      { key: 'recipient', label: '模拟接收对象', required: true },
    ],
    roles: serviceRoles,
    transitions: {
      draft: ['pendingReview'],
      pendingReview: ['approved', 'returned'],
      returned: ['draft'],
      approved: ['sentSimulated'],
    },
  },
  {
    kind: 'knowledge',
    name: '教学案例',
    fields: [
      {
        key: 'subject',
        label: '适用学科',
        required: true,
        choices: ['数学', '语文', '英语'],
      },
      { key: 'method', label: '方法说明', required: true },
      { key: 'conditions', label: '适用条件', required: true },
      { key: 'recordingUuid', label: '来源录音', required: true },
      {
        key: 'sanitized',
        label: '脱敏确认',
        required: true,
        choices: ['已核实脱敏'],
      },
    ],
    roles: educators,
    transitions: {
      draft: ['pendingReview'],
      pendingReview: ['published', 'returned'],
      returned: ['draft'],
      published: ['withdrawn'],
      withdrawn: ['draft'],
    },
  },
  {
    kind: 'pilot',
    name: '试点管理',
    fields: [
      { key: 'scope', label: '试点范围', required: true },
      { key: 'goal', label: '验收目标', required: true },
      { key: 'dueAt', label: '结束日期', required: true },
    ],
    roles: managers,
    transitions: {
      draft: ['inProgress'],
      inProgress: ['pendingReview'],
      pendingReview: ['completed', 'inProgress'],
    },
  },
  {
    kind: 'ticket',
    name: '服务工单',
    fields: [
      { key: 'deviceUuid', label: '关联设备' },
      {
        key: 'category',
        label: '故障类型',
        required: true,
        choices: ['设备', '采集', '软件'],
      },
      { key: 'description', label: '问题说明', required: true },
    ],
    roles: [...managers, 'deviceManager', 'support', 'teacher'],
    transitions: {
      draft: ['inProgress'],
      inProgress: ['pendingAcceptance'],
      pendingAcceptance: ['closed', 'inProgress'],
      closed: ['reopened'],
      reopened: ['inProgress'],
    },
  },
  {
    kind: 'help',
    name: '帮助与培训',
    fields: [
      {
        key: 'category',
        label: '分类',
        required: true,
        choices: ['设备操作', '录音管理', '业务流程', '培训'],
      },
      { key: 'body', label: '指南正文', required: true },
    ],
    roles: [...managers, 'support'],
    transitions: {
      draft: ['published'],
      published: ['withdrawn'],
      withdrawn: ['draft'],
    },
  },
];
export const moduleSpecs = Object.fromEntries(
  specs.map((spec) => [spec.kind, spec]),
) as Partial<Record<Kind, ModuleSpec>>;
export const pageKinds: Record<string, Kind> = {
  G01: 'organization',
  G02: 'policy',
  G04: 'modelConfig',
  G05: 'integration',
  G08: 'subscription',
  A04: 'evaluation',
  L03: 'zone',
  D05: 'model',
  C02: 'station',
  D06: 'inventory',
  D07: 'repair',
  A03: 'template',
  V05: 'correction',
  T02: 'lesson',
  T03: 'lesson',
  T06: 'plan',
  S01: 'student',
  S02: 'followUp',
  S03: 'feedback',
  K01: 'knowledge',
  K02: 'knowledge',
  K03: 'knowledge',
  K04: 'knowledge',
  O03: 'pilot',
  O04: 'pilot',
  H01: 'ticket',
  H02: 'help',
  G03: 'rule',
};
export interface Helpers {
  append: (
    s: Snapshot,
    kind: Kind,
    title: string,
    extra?: Partial<Entity>,
  ) => Entity;
  get: (s: Snapshot, uuid: string | undefined, kind?: Kind) => Entity;
  touch: (row: Entity, s: Snapshot) => void;
  task: (s: Snapshot, row: Entity, title: string) => void;
}
const need = (value: string | undefined, label: string) => {
  if (!value?.trim()) throw new Error(`请填写${label}`);
  return value.trim();
};
function checkedFields(
  s: Snapshot,
  kind: Kind,
  fields: Record<string, string>,
  helpers: Helpers,
) {
  const spec = moduleSpecs[kind];
  if (!spec) throw new Error('此模块尚未支持写入');
  for (const field of spec.fields) {
    const value = fields[field.key];
    if (field.required) need(value, field.label);
    if (value && field.choices && !field.choices.includes(value))
      throw new Error(`${field.label}选项无效`);
    if (
      field.number &&
      value &&
      (!Number.isInteger(Number(value)) || Number(value) < 0)
    )
      throw new Error(`${field.label}须为非负整数`);
    if (value && field.key.endsWith('Uuid')) helpers.get(s, value);
    if (
      value &&
      field.key === 'dueAt' &&
      (!Number.isFinite(Date.parse(value)) ||
        Date.parse(value) < Date.parse(s.now))
    )
      throw new Error('期限不能早于当前演示时间');
  }
  if (
    kind === 'modelConfig' &&
    (Number(fields.parallel) < 1 ||
      Number(fields.parallel) > 3 ||
      Number(fields.timeoutMs) <= 0)
  )
    throw new Error('并发须在1–3，超时须大于0');
  if (kind === 'subscription' && !Number.isFinite(Date.parse(fields.expiresAt)))
    throw new Error('到期时间无效');
  if (kind === 'zone') validatePolygon(fields.points);
  if (kind === 'feedback') {
    const source = helpers.get(s, fields.followUpUuid, 'followUp');
    if (source.status !== 'completed')
      throw new Error('反馈只能引用已完成且确认的跟进');
  }
  return Object.fromEntries(
    spec.fields
      .filter((field) => fields[field.key] !== undefined)
      .map((field) => [field.key, fields[field.key]]),
  );
}
export function moduleCommand(s: Snapshot, input: Command, h: Helpers): Entity {
  const row = input.uuid ? h.get(s, input.uuid) : undefined;
  const kind = row?.kind || input.kind;
  const spec = kind && moduleSpecs[kind];
  if (!kind || !spec) throw new Error('模块不可写入');
  if (
    !(
      input.action === 'moduleTraining' &&
      [...educators, 'support'].includes(s.context.role)
    ) &&
    !spec.roles.includes(s.context.role)
  )
    throw new Error('当前业务视角不能修改此模块');
  if (input.action === 'moduleCreate') {
    const fields = checkedFields(s, kind, input.fields || {}, h);
    const title = need(input.title, '标题');
    if (
      kind === 'organization' &&
      fields.type === '校区' &&
      s.entities.some(
        (e) =>
          e.kind === 'organization' &&
          e.fields?.type === '校区' &&
          e.title === title,
      )
    )
      throw new Error('校区名称不能重复');
    const created = h.append(s, kind, title, {
      ...(kind === 'organization' && fields.type === '校区'
        ? { campus: title }
        : {}),
      status: kind === 'inventory' ? 'active' : 'draft',
      note: input.note || '',
      fields: { ...fields, authorView: s.context.viewMemberUuid },
      sourceUuid: fields.recordingUuid || fields.deviceUuid,
      owner: input.memberUuid || s.context.viewMemberUuid,
      dueAt: fields.dueAt,
    });
    if (fields.recordingUuid) {
      const recording = h.get(s, fields.recordingUuid, 'recording');
      const version = recording.versions?.at(-1);
      const segment = version?.segments[0];
      if (version && segment)
        created.evidence = {
          recordingUuid: recording.uuid,
          transcriptVersionUuid: version.uuid,
          startMs: segment.startMs,
          endMs: segment.endMs,
          quote: segment.text,
        };
    }
    if (kind === 'repair') {
      const device = h.get(s, fields.deviceUuid, 'device');
      if (['retired', 'repair'].includes(device.status))
        throw new Error('设备当前不能报修');
      created.fields = {
        ...created.fields,
        previousLifecycle: device.status,
        previousMemberUuid: device.memberUuid,
      };
      device.status = 'repair';
      for (const binding of s.entities.filter(
        (e) => e.kind === 'binding' && e.deviceUuid === device.uuid && !e.endAt,
      )) {
        binding.endAt = s.now;
        binding.status = 'completed';
      }
      h.touch(device, s);
    }
    return created;
  }
  if (!row) throw new Error('对象不存在');
  if (input.action === 'moduleClone') {
    if (kind !== 'knowledge' || row.status !== 'published')
      throw new Error('只能为已发布案例建立修订草稿');
    return h.append(s, kind, row.title, {
      status: 'draft',
      campus: row.campus,
      memberUuid: row.memberUuid,
      evidence: row.evidence,
      sourceUuid: row.sourceUuid,
      fields: {
        ...checkedFields(s, kind, row.fields || {}, h),
        authorView: s.context.viewMemberUuid,
        previousVersionUuid: row.uuid,
      },
    });
  }
  if (input.action === 'moduleUpdate') {
    if (!['draft', 'returned', 'rejected', 'reopened'].includes(row.status))
      throw new Error('请先撤回或退回草稿后编辑，历史发布版不可直接修改');
    if (kind === 'organization' && input.fields?.parentUuid) {
      let parent = input.fields.parentUuid;
      const visited = new Set([row.uuid]);
      while (parent) {
        if (visited.has(parent)) throw new Error('组织上级不能形成循环');
        visited.add(parent);
        parent = h.get(s, parent, 'organization').fields?.parentUuid || '';
      }
    }
    const nextTitle = need(input.title, '标题');
    if (
      kind === 'organization' &&
      row.fields?.type === '校区' &&
      (nextTitle !== row.title || input.fields?.type !== '校区')
    )
      throw new Error(
        '已建校区的范围名称与类型不可改写，请保留历史归属并建立新组织',
      );
    row.title = nextTitle;
    row.fields = {
      ...Object.fromEntries(
        Object.entries(row.fields || {}).filter(
          ([key]) =>
            key.startsWith('history-') ||
            key.startsWith('version-') ||
            key === 'previousVersionUuid',
        ),
      ),
      ...checkedFields(s, kind, input.fields || {}, h),
      authorView: row.fields?.authorView || s.context.viewMemberUuid,
    };
    row.note = input.note || '';
    h.touch(row, s);
    return row;
  }
  if (input.action === 'inventory') {
    const amount = Number(input.note);
    if (kind !== 'inventory' || !Number.isInteger(amount) || amount === 0)
      throw new Error('数量变动须为非零整数');
    const current = Number(row.fields?.quantity || row.fields?.可用数量 || 0);
    if (current + amount < 0) throw new Error('可用库存不足');
    row.fields = { ...row.fields, quantity: String(current + amount) };
    h.touch(row, s);
    return row;
  }
  if (input.action === 'moduleTest') {
    if (!['rule', 'template', 'integration', 'modelConfig'].includes(kind))
      throw new Error('此模块没有样本测试');
    const fields = row.fields || {};
    if (kind === 'modelConfig') {
      const results = processingExperiment(
        Number(fields.parallel),
        Number(fields.timeoutMs),
      );
      row.fields = {
        ...fields,
        tested: 'true',
        testResult: JSON.stringify(results),
        testedAt: s.now,
      };
      h.touch(row, s);
      return row;
    }
    if (kind === 'integration') {
      const data = parseSync(fields.sourceJson);
      row.fields = {
        ...fields,
        tested: 'true',
        preview: JSON.stringify(
          data.map((source) => {
            const target = s.entities.find(
              (e) =>
                e.kind === fields.targetKind &&
                e.campus === row.campus &&
                e.fields?.externalKey === source.key,
            );
            return {
              key: source.key,
              title: source.title,
              targetUuid: target?.uuid,
              targetRevision: target?.revision,
              action: target
                ? fields.strategy === '跳过'
                  ? '跳过冲突'
                  : '更新'
                : '创建',
            };
          }),
        ),
      };
      h.touch(row, s);
      return row;
    }
    const corpus =
      fields.example ||
      s.entities
        .find((e) => e.kind === 'recording')
        ?.versions?.[0]?.segments.map((e) => e.text)
        .join(' ') ||
      '';
    row.fields = {
      ...fields,
      tested: 'true',
      testResult: fields.keyword
        ? `模拟关键词匹配：${corpus.includes(fields.keyword) ? '命中' : '未命中'}；排除条件仅供人工参考`
        : '模拟输出结构完整',
    };
    h.touch(row, s);
    return row;
  }
  if (input.action === 'moduleTransition') {
    if (kind === 'ticket')
      throw new Error('请使用服务工单的分派、解决方案与独立验收流程');
    const target = need(input.conclusion, '目标状态');
    if (!spec.transitions[row.status]?.includes(target))
      throw new Error('非法状态流转');
    if (
      [
        'completed',
        'closed',
        'accepted',
        'approved',
        'published',
        'sentSimulated',
        'pendingAcceptance',
      ].includes(target)
    )
      need(input.note, '处理或审核说明');
    if (
      ['published', 'approved', 'accepted', 'completed', 'closed'].includes(
        target,
      ) &&
      ['teacher', 'studentService', 'support', 'deviceManager'].includes(
        s.context.role,
      )
    )
      throw new Error('请切换有审核或验收权限的负责人视角');
    if (
      ['knowledge', 'feedback'].includes(kind) &&
      ['published', 'approved'].includes(target) &&
      row.fields?.authorView === s.context.viewMemberUuid
    )
      throw new Error('作者不能审核自己提交的版本，请选择另一位审核人员');
    if (
      ['rule', 'template', 'modelConfig'].includes(kind) &&
      target === 'published' &&
      row.fields?.tested !== 'true'
    )
      throw new Error('请先进行样本测试，修改草稿会使测试失效');
    if (kind === 'knowledge' && target === 'published') {
      checkedFields(s, kind, row.fields || {}, h);
      if (!row.evidence) throw new Error('案例缺少可追溯的来源片段');
    }
    if (kind === 'plan' && target === 'completed') {
      if (!row.fields?.practice) throw new Error('请先登记实践与阶段反馈');
    }
    if (kind === 'correction' && target === 'accepted') {
      const recording = h.get(s, row.fields?.recordingUuid, 'recording');
      const old = recording.versions?.at(-1);
      if (!old) throw new Error('来源转写不存在');
      recording.versions?.push({
        uuid: crypto.randomUUID(),
        createdAt: s.now,
        segments: old.segments.map((seg, i) => ({
          ...seg,
          uuid: crypto.randomUUID(),
          text:
            i === 0 ? need(row.fields?.correctedText, '更正文本') : seg.text,
        })),
      });
      h.touch(recording, s);
      for (const related of s.entities.filter(
        (e) => e.evidence?.recordingUuid === recording.uuid,
      ))
        related.fields = {
          ...related.fields,
          sourceChanged: '来源更正已接受，请核实影响',
        };
    }
    if (
      ['repair', 'ticket'].includes(kind) &&
      target === 'closed' &&
      row.fields?.deviceUuid
    ) {
      const device = h.get(s, row.fields.deviceUuid, 'device');
      if (kind === 'repair') device.status = 'stock';
      h.touch(device, s);
    }
    if (kind === 'pilot' && target === 'completed')
      row.fields = {
        ...row.fields,
        report: JSON.stringify({
          generatedAt: s.now,
          goal: row.fields?.goal,
          records: s.entities.filter((e) => e.kind === 'recording').length,
          openCases: s.entities.filter(
            (e) => e.kind === 'case' && e.status !== 'closed',
          ).length,
          note: input.note,
          simulated: true,
        }),
      };
    row.fields = {
      ...row.fields,
      [`history-${row.revision}`]: JSON.stringify({
        from: row.status,
        to: target,
        note: input.note || '',
        at: s.now,
        by: s.context.viewMemberUuid,
      }),
    };
    if (kind === 'organization' && target === 'disabled') {
      const activeChildren = s.entities.some(
        (e) =>
          e.kind === 'organization' &&
          e.fields?.parentUuid === row.uuid &&
          e.status !== 'disabled',
      );
      const members =
        row.fields?.type === '校区' &&
        s.entities.some(
          (e) =>
            e.kind === 'teacher' &&
            e.campus === row.campus &&
            e.status === 'active',
        );
      if (activeChildren || members)
        throw new Error('请先处理有效下级组织与在职业务人员，不能直接停用');
    }
    if (
      kind === 'station' &&
      target === 'disabled' &&
      s.entities.some(
        (e) =>
          e.kind === 'batch' &&
          e.campus === row.campus &&
          !['completed', 'failed', 'cancelled'].includes(e.status),
      )
    )
      throw new Error('站点仍有活动采集，请先暂停或完成任务');
    if (kind === 'model' && target === 'published') {
      checkedFields(s, kind, row.fields || {}, h);
      for (const device of s.entities.filter(
        (e) => e.kind === 'device' && e.model === row.title,
      )) {
        device.supportsRemote = row.fields?.remote === '已验证';
        h.touch(device, s);
      }
    }
    if (
      ['published', 'approved', 'sentSimulated', 'completed'].includes(target)
    )
      row.fields = {
        ...row.fields,
        [`version-${row.revision}`]: JSON.stringify({
          title: row.title,
          fields: Object.fromEntries(
            (spec.fields || []).map((field) => [
              field.key,
              row.fields?.[field.key],
            ]),
          ),
          status: target,
          reviewer: s.context.viewMemberUuid,
          at: s.now,
        }),
      };
    if (
      kind === 'knowledge' &&
      target === 'published' &&
      row.fields?.previousVersionUuid
    ) {
      const previous = h.get(s, row.fields.previousVersionUuid, 'knowledge');
      previous.status = 'withdrawn';
      previous.fields = { ...previous.fields, supersededBy: row.uuid };
      h.touch(previous, s);
    }
    if (
      ['modelConfig', 'subscription', 'policy', 'zone', 'integration'].includes(
        kind,
      ) &&
      target === 'published'
    ) {
      checkedFields(s, kind, row.fields || {}, h);
      if (kind === 'integration' && row.fields?.tested !== 'true')
        throw new Error('请先预览同步差异');
    }
    if (
      ['modelConfig', 'subscription', 'policy'].includes(kind) &&
      target === 'published'
    )
      for (const previous of s.entities.filter(
        (e) =>
          e.kind === kind &&
          e.uuid !== row.uuid &&
          e.status === 'published' &&
          (kind !== 'policy' ||
            (e.fields?.role === row.fields?.role &&
              e.fields?.kind === row.fields?.kind)),
      )) {
        previous.status = 'disabled';
        h.touch(previous, s);
      }
    row.status = target;
    row.note = input.note || row.note;
    h.touch(row, s);
    if (['inProgress', 'confirmed', 'pendingReview'].includes(target))
      h.task(s, row, `${spec.name}：${row.title}`);
    if (
      [
        'closed',
        'completed',
        'published',
        'approved',
        'accepted',
        'sentSimulated',
      ].includes(target)
    )
      for (const task of s.entities.filter(
        (e) => e.kind === 'task' && e.sourceUuid === row.uuid,
      )) {
        task.status = 'completed';
        h.touch(task, s);
      }
    return row;
  }
  if (input.action === 'modulePractice') {
    if (kind !== 'plan' || row.status !== 'inProgress')
      throw new Error('计划尚未进入实践阶段');
    row.fields = { ...row.fields, practice: need(input.note, '实践与反馈') };
    h.touch(row, s);
    return row;
  }
  if (input.action === 'knowledgeReuse') {
    if (kind !== 'knowledge' || row.status !== 'published')
      throw new Error('只能引用已发布案例');
    const plan = h.get(s, input.fields?.planUuid, 'plan');
    if (plan.fields?.[`knowledge-${row.uuid}`]) return row;
    plan.fields = {
      ...plan.fields,
      [`knowledge-${row.uuid}`]: `${row.uuid}@${row.revision}`,
    };
    row.fields = {
      ...row.fields,
      reuses: String(Number(row.fields?.reuses || 0) + 1),
    };
    h.touch(plan, s);
    h.touch(row, s);
    return row;
  }
  if (input.action === 'moduleTraining') {
    if (kind !== 'help' || row.status !== 'published')
      throw new Error('只有已发布指南可登记培训');
    row.fields = {
      ...row.fields,
      [`trained-${s.context.viewMemberUuid}`]: s.now,
    };
    const teacher = s.entities.find(
      (e) => e.kind === 'teacher' && e.memberUuid === s.context.viewMemberUuid,
    );
    if (teacher) {
      teacher.fields = {
        ...teacher.fields,
        [`training-${row.uuid}`]: `${row.title} ${s.now}`,
      };
      h.touch(teacher, s);
    }
    h.touch(row, s);
    return row;
  }
  if (input.action === 'moduleSync') {
    if (
      kind !== 'integration' ||
      row.status !== 'published' ||
      !row.fields?.preview
    )
      throw new Error('请先预览并发布模拟同步配置');
    const data = parseSync(row.fields.sourceJson);
    const signature = JSON.stringify([
      row.fields.sourceJson,
      row.fields.strategy,
      row.campus,
      row.fields.targetKind,
    ]);
    const applied = JSON.parse(row.fields.syncApplied || 'null') as {
      signature: string;
      targets: { uuid: string; revision: number }[];
    } | null;
    if (
      applied?.signature === signature &&
      applied.targets.every((r) =>
        s.entities.some((e) => e.uuid === r.uuid && e.revision === r.revision),
      )
    )
      return row;
    const preview = JSON.parse(row.fields.preview) as {
      key: string;
      targetUuid?: string;
      targetRevision?: number;
    }[];
    const targets: { uuid: string; revision: number }[] = [];
    for (const source of data) {
      const targetKind = row.fields.targetKind as Kind;
      const existing = s.entities.find(
        (e) =>
          e.kind === targetKind &&
          e.campus === row.campus &&
          e.fields?.externalKey === source.key,
      );
      const frozen = preview.find((p) => p.key === source.key);
      if (
        !frozen ||
        (existing &&
          row.fields.strategy === '确认更新' &&
          (frozen.targetUuid !== existing.uuid ||
            frozen.targetRevision !== existing.revision)) ||
        (!existing && frozen.targetUuid)
      )
        throw new Error('同步目标已变化，请重新预览差异后确认');
      if (existing) {
        if (row.fields.strategy === '确认更新') {
          existing.title = source.title;
          h.touch(existing, s);
        }
        targets.push({ uuid: existing.uuid, revision: existing.revision });
      } else {
        const created = h.append(s, targetKind, source.title, {
          status: targetKind === 'teacher' ? 'active' : 'draft',
          campus: row.campus,
          memberUuid:
            targetKind === 'teacher' ? crypto.randomUUID() : undefined,
          fields: { externalKey: source.key, syncSource: row.uuid },
        });
        targets.push({ uuid: created.uuid, revision: created.revision });
      }
    }
    row.fields = {
      ...row.fields,
      syncApplied: JSON.stringify({ signature, targets }),
    };
    row.fields = { ...row.fields, lastSync: s.now };
    h.touch(row, s);
    return row;
  }
  throw new Error('此模块动作尚未实现');
}

export function policyAllows(
  s: Snapshot,
  kind: Kind,
  action: 'read' | 'write',
) {
  return !s.entities.some(
    (e) =>
      e.kind === 'policy' &&
      e.status === 'published' &&
      e.fields?.role === s.context.role &&
      e.fields.kind === kind &&
      e.fields[action] === '禁止',
  );
}
function parseSync(text: string | undefined): { key: string; title: string }[] {
  let data: unknown;
  try {
    data = JSON.parse(text || '');
  } catch {
    throw new Error('来源JSON无效');
  }
  if (
    !Array.isArray(data) ||
    data.length > 100 ||
    data.some(
      (value) =>
        !value ||
        typeof value.key !== 'string' ||
        typeof value.title !== 'string' ||
        !value.key.trim() ||
        !value.title.trim(),
    )
  )
    throw new Error('来源须为最多100条含key和title的数组');
  if (new Set(data.map((value) => value.key)).size !== data.length)
    throw new Error('来源key不能重复');
  return data;
}
export function validatePolygon(text: string) {
  let points: unknown;
  try {
    points = JSON.parse(text);
  } catch {
    throw new Error('坐标JSON无效');
  }
  if (
    !Array.isArray(points) ||
    points.length < 3 ||
    points.length > 30 ||
    points.some(
      (p) =>
        !Array.isArray(p) ||
        p.length !== 2 ||
        p.some((v) => typeof v !== 'number' || v < 0 || v > 1),
    )
  )
    throw new Error('坐标需要3–30个范围0–1的二维点');
  const p = points as number[][];
  const cross = (a: number[], b: number[], c: number[]) =>
    (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  let area = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i],
      b = p[(i + 1) % p.length];
    area += a[0] * b[1] - a[1] * b[0];
    for (let j = i + 2; j < p.length; j++) {
      if (i === 0 && j === p.length - 1) continue;
      const c = p[j],
        d = p[(j + 1) % p.length];
      if (
        cross(a, b, c) * cross(a, b, d) <= 0 &&
        cross(c, d, a) * cross(c, d, b) <= 0
      )
        throw new Error('围栏不能自相交');
    }
  }
  if (Math.abs(area) < 0.001) throw new Error('围栏面积过小');
  return p;
}

export function insidePolygon(x: number, y: number, points: number[][]) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}
